// ─────────────────────────────────────────────────────────
// services/repository.ts — Data access layer
//
// Phase 4 changes:
//   + fbSave  → writes to IDB immediately, then Supabase
//   + fbDel   → deletes from IDB immediately, then Supabase
//   + fbLoad  → after Supabase fetch, writes result to IDB
//   + fbSetDirect → writes to IDB immediately, then Supabase
//
// IDB is now the single source of truth for reads.
// Supabase is the sync target (gated by USE_SUPABASE_DB flag).
// All function signatures unchanged — zero module page impact.
// ─────────────────────────────────────────────────────────
import { supabase }                 from '@/supabase'
import { encryptDoc, decryptDoc }   from './encryption'
import { useUIStore }                from '@/store/uiStore'
import { FLAGS }                     from '@/lib/flags'
import { idbPut, idbDelete }         from './idb'
import { idbWrite }                  from './idbSync'
import type {
  Project, BOQItem, Milestone, DPREntry, Tender,
  EMD, Receivable, RetentionRelease, Expense, CashFlowEntry,
  PettyCashEntry, BankReconEntry, Vendor, PurchaseOrder,
  Quotation, ContractorInvoice, Employee, AttSheet,
  SalaryRecord, Advance, Insurance, TDSEntry, EInvoice,
  Task, AuditEntry, Notification, Brand,
} from '@/types'

export interface LoadedData {
  proj: Project[]; boq: BOQItem[]; milestones: Milestone[]; daily: DPREntry[]
  tender: Tender[]; emd: EMD[]; recv: Receivable[]; ret: RetentionRelease[]
  exp: Expense[]; cf: CashFlowEntry[]; petty: PettyCashEntry[]
  recon: BankReconEntry[]; vendor: Vendor[]; po: PurchaseOrder[]
  quotations: Quotation[]; invoice: ContractorInvoice[]; emp: Employee[]
  att: Record<string, AttSheet>; sal: Record<string, SalaryRecord>
  adv: Advance[]; ins: Insurance[]; tds: TDSEntry[]; einvoices: EInvoice[]
  task: Task[]; audit: AuditEntry[]; notif: Notification[]; brand: Partial<Brand>
}

export const EMPTY_LOADED_DATA: LoadedData = {
  proj: [], boq: [], milestones: [], daily: [], tender: [], emd: [],
  recv: [], ret: [], exp: [], cf: [], petty: [], recon: [], vendor: [],
  po: [], quotations: [], invoice: [], emp: [], att: {}, sal: {},
  adv: [], ins: [], tds: [], einvoices: [], task: [], audit: [], notif: [], brand: {},
}

const UNENCRYPTED = new Set(['brand', 'notif'])

async function dec<T>(data: unknown, id: string): Promise<T> {
  const plain = await decryptDoc(data as T | { _enc: string })
  return { ...(plain as object), id } as T
}

async function loadCol<T>(
  rows: Array<{ collection: string; id: string; data: unknown }>, col: string
): Promise<T[]> {
  const out: T[] = []
  for (const r of rows.filter(r => r.collection === col))
    out.push(await dec<T>(r.data, r.id))
  return out
}

// ── Resolve which IDB store a collection maps to ──────────
function idbStore(col: string): import('./idb').IDBCollection | null {
  const MAP: Record<string, import('./idb').IDBCollection> = {
    proj: 'proj', boq: 'boq', milestones: 'milestones', daily: 'daily',
    tender: 'tender', emd: 'emd', recv: 'recv', ret: 'ret', exp: 'exp',
    cf: 'cf', petty: 'petty', recon: 'recon', vendor: 'vendor', po: 'po',
    quotations: 'quotations', invoice: 'invoice', emp: 'emp', adv: 'adv',
    ins: 'ins', tds: 'tds', einvoices: 'einvoices', task: 'task',
    audit: 'audit', notif: 'notif',
    attendance: 'attendance', salaries: 'salaries', brand: 'brand',
  }
  return MAP[col] ?? null
}

// ─────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────

export async function fbSave(
  col: string, id: string, data: object, companyId: string
): Promise<void> {
  // 1. Write to IDB immediately (offline-safe, instant)
  const store = idbStore(col)
  if (store) {
    idbPut(store, { id, ...data }).catch(e => console.warn('[idb] fbSave put:', e))
  }

  // 2. Update sessionStorage fallback
  const { persist } = await import('./offline')
  persist()

  // 3. Flag gate: skip Supabase if disabled
  if (!FLAGS.USE_SUPABASE_DB) {
    const ui = useUIStore.getState()
    ui.setSyncing(true, 'Saved locally')
    setTimeout(() => ui.setSyncing(false), 2000)
    return
  }

  // 4. Sync to Supabase
  const { setSyncing } = useUIStore.getState()
  setSyncing(true, 'Saving…')
  try {
    if (col === 'users') {
      const d = data as Record<string, unknown>
      const { error } = await supabase.from('users').upsert({
        id, company_id: (d.companyId ?? d.company_id ?? '') as string,
        role: (d.role ?? 'master_admin') as string,
        name: (d.name ?? d.displayName ?? '') as string,
        email: (d.email ?? '') as string,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id' })
      if (error) throw error
    } else {
      const payload = UNENCRYPTED.has(col) ? data : await encryptDoc(data, col)
      const { error } = await supabase.from('docs').upsert({
        company_id: companyId, collection: col, id,
        data: payload, updated_at: new Date().toISOString(),
      }, { onConflict: 'company_id,collection,id' })
      if (error) throw error
    }
    setSyncing(true, 'Saved ✓')
    setTimeout(() => setSyncing(false), 2000)
  } catch (e) {
    if (!navigator.onLine) {
      const { enqueueWrite } = await import('./offline')
      await enqueueWrite(col, id, data, companyId)
      setSyncing(true, 'Saved offline — will sync on reconnect')
    } else {
      setSyncing(true, 'Save error')
    }
    setTimeout(() => setSyncing(false), 3000)
    throw e
  }
}

export async function fbSetDirect(
  colName: string, docId: string, data: object, companyId: string
): Promise<void> {
  // Write to IDB first
  const store = idbStore(colName)
  if (store) {
    idbPut(store, { id: docId, ...data }).catch(() => {})
  }

  if (!FLAGS.USE_SUPABASE_DB) return

  try {
    const skip = colName === 'brand'
    const payload = skip ? data : await encryptDoc(data, colName)
    const { error } = await supabase.from('docs').upsert({
      company_id: companyId, collection: colName, id: docId,
      data: payload, updated_at: new Date().toISOString(),
    }, { onConflict: 'company_id,collection,id' })
    if (error) console.warn('fbSetDirect error:', error.message)
  } catch (e) { console.warn('fbSetDirect error:', e) }
}

export async function fbDel(col: string, id: string, companyId: string): Promise<void> {
  // Delete from IDB first
  const store = idbStore(col)
  if (store) {
    idbDelete(store, id).catch(() => {})
  }

  // Update session fallback
  const { persist } = await import('./offline')
  persist()

  if (!FLAGS.USE_SUPABASE_DB) return

  try {
    if (col === 'users') {
      const { error } = await supabase.from('users').delete().eq('id', id)
      if (error) console.warn('fbDel users error:', error.message)
    } else {
      // Soft delete — set is_deleted=true instead of a hard DELETE.
      // Deleted rows are filtered from SELECT by RLS + fbLoad query.
      const { error } = await supabase.from('docs')
        .update({ is_deleted: true, updated_at: new Date().toISOString() })
        .eq('company_id', companyId).eq('collection', col).eq('id', id)
      if (error) console.warn('fbDel error:', error.message)
    }
  } catch (e) { console.warn('fbDel error:', e) }
}

export async function fbLoad(companyId: string): Promise<LoadedData> {
  if (!FLAGS.USE_SUPABASE_DB) {
    // IDB was already loaded in loginSuccess — return empty signal
    return EMPTY_LOADED_DATA
  }

  const { data: rows, error } = await supabase
    .from('docs').select('collection, id, data')
    .eq('company_id', companyId).eq('is_deleted', false)
  if (error) throw error
  const r = rows ?? []

  const [
    proj, tender, emd, recv, exp, cf, vendor, invoice,
    emp, adv, ins, task, recon, tds, einvoices, daily,
    ret, petty, po, milestones, quotations, boq, notif,
  ] = await Promise.all([
    loadCol<Project>(r, 'proj'),           loadCol<Tender>(r, 'tender'),
    loadCol<EMD>(r, 'emd'),               loadCol<Receivable>(r, 'recv'),
    loadCol<Expense>(r, 'exp'),           loadCol<CashFlowEntry>(r, 'cf'),
    loadCol<Vendor>(r, 'vendor'),         loadCol<ContractorInvoice>(r, 'invoice'),
    loadCol<Employee>(r, 'emp'),          loadCol<Advance>(r, 'adv'),
    loadCol<Insurance>(r, 'ins'),         loadCol<Task>(r, 'task'),
    loadCol<BankReconEntry>(r, 'recon'),  loadCol<TDSEntry>(r, 'tds'),
    loadCol<EInvoice>(r, 'einvoices'),    loadCol<DPREntry>(r, 'daily'),
    loadCol<RetentionRelease>(r, 'ret'),  loadCol<PettyCashEntry>(r, 'petty'),
    loadCol<PurchaseOrder>(r, 'po'),      loadCol<Milestone>(r, 'milestones'),
    loadCol<Quotation>(r, 'quotations'),  loadCol<BOQItem>(r, 'boq'),
    loadCol<Notification>(r, 'notif'),
  ])

  const att: Record<string, AttSheet> = {}
  for (const row of r.filter(x => x.collection === 'attendance')) {
    const d = await decryptDoc(row.data as { rows?: AttSheet } & AttSheet | { _enc: string })
    att[row.id] = (d as { rows?: AttSheet } & AttSheet).rows ?? d
  }

  const sal: Record<string, SalaryRecord> = {}
  for (const row of r.filter(x => x.collection === 'salaries')) {
    sal[row.id] = await decryptDoc(row.data as SalaryRecord | { _enc: string })
  }

  const auditRows = r.filter(x => x.collection === 'audit')
  const audit: AuditEntry[] = []
  for (const row of auditRows) {
    const d = await decryptDoc(row.data as AuditEntry | { _enc: string })
    audit.push({ ...d as AuditEntry, id: row.id })
  }
  audit.sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? '')).splice(200)

  const brandRow = r.find(x => x.collection === 'brand' && x.id === 'config')
  const brand = (brandRow?.data ?? {}) as Partial<Brand>
  brand.logoUrl       = brand.logoUrl       ?? ''
  brand.slipHeaderUrl = brand.slipHeaderUrl ?? ''

  const result: LoadedData = {
    proj, boq, milestones, daily, tender, emd, recv, ret,
    exp, cf, petty, recon, vendor, po, quotations, invoice,
    emp, att, sal, adv, ins, tds, einvoices, task, audit, notif, brand,
  }

  // Write fresh Supabase data to IDB (async, non-blocking)
  idbWrite(companyId, result).catch(e => console.warn('[idb] fbLoad write failed:', e))

  return result
}

// ── User / Company lookups ────────────────────────────────
export async function getUserDoc(uid: string) {
  if (!FLAGS.USE_SUPABASE_DB) return null
  const { data } = await supabase.from('users').select('*').eq('id', uid).single()
  if (!data) return null
  return { id: data.id, name: data.name, role: data.role, companyId: data.company_id, email: data.email }
}

export async function getCompanyDoc(companyId: string) {
  if (!FLAGS.USE_SUPABASE_DB) return { id: companyId, status: 'active' }
  const { data } = await supabase.from('companies').select('*').eq('id', companyId).single()
  return data ?? { id: companyId, status: 'active' }
}

export async function getCompanyUsers(companyId: string, _uid: string) {
  if (!FLAGS.USE_SUPABASE_DB) return []
  const { data } = await supabase.from('users').select('*').eq('company_id', companyId)
  return (data ?? []).map(u => ({
    id: u.id, name: u.name, email: u.email, role: u.role,
    companyId: u.company_id, invite_pending: u.invite_pending ?? false,
  }))
}

export async function saveCompanyDoc(companyId: string, data: Record<string, unknown>): Promise<void> {
  if (!FLAGS.USE_SUPABASE_DB) return
  const { error } = await supabase.from('companies').upsert(
    { id: companyId, ...data, updated_at: new Date().toISOString() }, { onConflict: 'id' }
  )
  if (error) throw error
}

export interface CompanyRow {
  id: string; name: string; proprietor?: string; gstin?: string
  email?: string; phone?: string; city?: string; status: string; plan: string; createdAt?: string
}

export async function getAllCompanies(): Promise<CompanyRow[]> {
  if (!FLAGS.USE_SUPABASE_DB) return []
  const { data, error } = await supabase.from('companies').select('*').order('name')
  if (error) throw error
  return (data ?? []).map(c => ({
    id: c.id, name: c.name, proprietor: c.proprietor ?? '', gstin: c.gstin ?? '',
    email: c.email ?? '', phone: c.phone ?? '', city: c.city ?? '',
    status: c.status, plan: c.plan, createdAt: c.created_at,
  }))
}

export async function getPlatformStats() {
  if (!FLAGS.USE_SUPABASE_DB) return { totalCompanies: 0, planBreakdown: {}, recentSignups: [] }
  const [{ count: total }, { data: planRows }, { data: recentRows }] = await Promise.all([
    supabase.from('companies').select('*', { count: 'exact', head: true }),
    supabase.from('companies').select('plan'),
    supabase.from('companies').select('id, name, created_at').order('created_at', { ascending: false }).limit(5),
  ])
  const planBreakdown: Record<string, number> = {}
  ;(planRows ?? []).forEach(r => { planBreakdown[r.plan] = (planBreakdown[r.plan] ?? 0) + 1 })
  return {
    totalCompanies: total ?? 0, planBreakdown,
    recentSignups: (recentRows ?? []).map(r => ({ id: r.id, name: r.name, createdAt: r.created_at })),
  }
}

export async function updateCompanyPlan(id: string, plan: string): Promise<void> {
  if (!FLAGS.USE_SUPABASE_DB) return
  const { error } = await supabase.from('companies').update({ plan, updated_at: new Date().toISOString() }).eq('id', id)
  if (error) throw error
}

export async function updateCompanyStatus(id: string, status: string): Promise<void> {
  if (!FLAGS.USE_SUPABASE_DB) return
  const { error } = await supabase.from('companies').update({ status, updated_at: new Date().toISOString() }).eq('id', id)
  if (error) throw error
}
